/**
 * Resolves the initial ABP payment amount from a URL query string parameter.
 *
 * When the merchant selects an amount in the monocle pre-payment modal, it is
 * passed to webregister as the `amount` query param. This function parses and
 * validates that value before it is dispatched to Redux.
 *
 * The URL parameter is untrusted input (a user can modify the address bar), so
 * this function applies the same constraints as the `AccountBalanceSetupModal`
 * `CurrencyInput` (`nonZero`, `nonNegative`, `max: balance`, `decimalPlaces: 2`):
 * - rounded to 2 decimal places to prevent sub-cent values
 * - must be > 0 after rounding
 * - capped to `absBalance` (never exceeds what the customer actually owes)
 *
 * The Monoliph backend (`VendCustomerPayBalanceForm`) is the authoritative last
 * line of defence and validates `min: 0 / max: $customer_balance` server-side.
 *
 * @param prefilledAmountParam - Raw string value of the `amount` URL param, or
 *   `null` / empty string when the param is absent (e.g. direct navigation or
 *   older monocle builds that don't yet send the param).
 * @param absBalance - The customer's actual outstanding balance as a positive
 *   number (already absolute-valued). Acts as the upper cap and the fallback
 *   value when the param is invalid.
 * @returns The resolved amount to use as the initial ABP payment value:
 *   - `prefilledAmountParam` parsed, rounded to 2 decimal places, and capped to
 *     `absBalance` when it is a valid positive finite value.
 *   - `absBalance` (full balance) for any absent, non-numeric, zero, negative,
 *     or out-of-range value.
 */

import { round } from 'vend-number'

export const resolveAbpInitialAmount = (
  prefilledAmountParam: string | null,
  absBalance: number
): number => {
  const parsed = Number(prefilledAmountParam)
  if (!prefilledAmountParam || !Number.isFinite(parsed)) return absBalance

  // Round to 2 decimal places — same constraint as AccountBalanceSetupModal's
  // `decimalPlaces: 2` validator — to prevent sub-cent amounts from a crafted URL.
  const rounded = Number(round(parsed.toString(), 2))

  return rounded > 0 ? Math.min(rounded, absBalance) : absBalance
}
